import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

import { DashboardError, DashboardPage } from '../../components/JobsAccount';
import { book } from '../../routers/books';
import {
  IResumeContactRequest,
  getResumeDatabaseErrorMessage,
  useGetResumeDatabaseAccessQuery,
  useGetResumeDetailQuery,
  useMintEmployerResumeFileMutation,
} from '../../services/serviceResume';
import { isForbidden, isNotFound } from '../../utils/jobAccessError';
import { ResumeDatabaseSuspended, ResumeDatabaseTerms } from './accessPanels';
import { IntroductionDialog } from './introductionDialog';
import { ResumeDatabaseUpsell } from './upsell';

const requestConfirmation = (request: IResumeContactRequest): {
  severity: 'success' | 'warning' | 'info';
  text: string;
} => {
  if (request.needsRetry) {
    return {
      severity: 'warning',
      text: 'Your request was saved, but the notification email could not be queued. Send it again '
        + 'to retry. It will not use another introduction.',
    };
  }
  if (request.status === 1) {
    return {
      severity: 'success',
      text: 'This candidate has already approved you. Their contact details are below. No new '
        + 'introduction was used.',
    };
  }
  if (request.status === 0) {
    return {
      severity: 'success',
      text: 'Your introduction request is on its way. The candidate decides whether to share their '
        + 'contact details.',
    };
  }
  return {
    severity: 'info',
    text: 'We already have a record of this request. No new introduction was used.',
  };
};

export const ResumeDatabaseDetail = () => {
  const { rguid } = useParams<{ rguid: string }>();
  const detail = useGetResumeDetailQuery(rguid, { skip: !rguid });
  const access = useGetResumeDatabaseAccessQuery();
  const [mintFile, mintResult] = useMintEmployerResumeFileMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState<IResumeContactRequest | null>(null);

  const state = access.data?.state;
  const isSuspended = state === 'suspended';
  // A profile is not loaded before the employer accepts the current terms.
  const needsTerms = !isSuspended && state === 'termsRequired';
  const blocked = isSuspended || needsTerms;
  const accessDenied = !blocked && isForbidden(detail.error);
  const notFound = isNotFound(detail.error);
  const approved = Boolean(detail.data?.contactRequestApproved);
  const canRequest = state === 'granted';
  const detailMessage = detail.error ? getResumeDatabaseErrorMessage(detail.error) : null;
  const mintMessage = mintResult.error
    ? getResumeDatabaseErrorMessage(mintResult.error)
      || 'We could not prepare that download. Please try again.'
    : null;

  const downloadFile = async () => {
    try {
      const file = await mintFile(rguid).unwrap();
      if (file.downloadUrl) window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // The parsed policy error is rendered beside the download control.
    }
  };

  return (
    <DashboardPage
      title={detail.data?.displayName || 'Resume'}
      isLoading={detail.isLoading || access.isLoading}
      noAccessComponent={accessDenied ? <ResumeDatabaseUpsell /> : undefined}
      actions={<Button component={Link} to={book.resumeDatabase} variant="outlined">Back to search</Button>}
    >
      {isSuspended && access.data ? <ResumeDatabaseSuspended access={access.data} /> : null}

      {needsTerms && access.data ? <ResumeDatabaseTerms access={access.data} /> : null}

      {!blocked && notFound ? (
        <Alert severity="info">
          This resume is no longer available. The candidate may have made it private or withdrawn
          your access.
        </Alert>
      ) : null}

      {!blocked && !accessDenied && !notFound ? (
        <>
          {detailMessage ? (
            <Alert severity="warning" sx={{ mb: 2 }}>{detailMessage}</Alert>
          ) : (
            <DashboardError error={detail.error} fallback="We could not load this resume." />
          )}
        </>
      ) : null}

      {!blocked && submitted ? (
        <Alert severity={requestConfirmation(submitted).severity} sx={{ mb: 2 }}>
          {requestConfirmation(submitted).text}
        </Alert>
      ) : null}

      {!blocked && detail.data ? (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ borderRadius: 0, p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography component="h2" variant="h5">{detail.data.headline || 'CPG professional'}</Typography>
              {detail.data.identityHidden ? (
                <Chip icon={<VisibilityOffOutlinedIcon />} label="Name hidden" size="small" />
              ) : null}
            </Stack>
            <Typography color="text.secondary">{detail.data.primaryLocation || 'Location not listed'}</Typography>
            <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>{detail.data.overview || 'No overview provided.'}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 0, p: 3 }}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>Contact &amp; resume file</Typography>
            {!approved ? (
              <Stack alignItems="flex-start" spacing={1.5}>
                <LockOutlinedIcon color="primary" />
                <Typography>
                  Email, phone, address, and the uploaded resume file stay hidden until this
                  candidate approves an introduction for one of your Featured listings.
                  {!detail.data.identityHidden
                    ? ' A Public profile shows their name, but it does not share their contact details.'
                    : ''}
                </Typography>
                {canRequest ? (
                  <Button variant="contained" onClick={() => setDialogOpen(true)}>
                    Request an introduction
                  </Button>
                ) : (
                  <Typography color="text.secondary">
                    An active Featured job listing is required to request an introduction.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography><strong>Email:</strong> {detail.data.email || 'Not listed'}</Typography>
                <Typography><strong>Phone:</strong> {detail.data.phone || 'Not listed'}</Typography>
                <Typography><strong>Address:</strong> {detail.data.address || 'Not listed'}</Typography>
                {detail.data.file.hasFile ? (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={mintResult.isLoading}
                    onClick={downloadFile}
                  >
                    {detail.data.file.fileName || 'resume'}
                  </Button>
                ) : (
                  <Typography color="text.secondary">This candidate has not uploaded a resume file.</Typography>
                )}
                {mintMessage ? <Alert severity="error">{mintMessage}</Alert> : null}
                <Typography color="success.main" variant="caption">
                  This candidate approved your introduction request. They can withdraw access at any
                  time.
                </Typography>
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 0, p: 3 }}>
            <Typography component="h2" variant="h6">Work history</Typography>
            <Stack divider={<Divider flexItem />}>
              {detail.data.workHistory.map((row) => (
                <Box key={row.previousEmployerId} sx={{ py: 2 }}>
                  <Typography fontWeight={800}>{row.jobTitle || 'Role'} · {row.employerName}</Typography>
                  <Typography color="text.secondary" variant="body2">
                    {row.startDate ? moment(row.startDate).format('MMM YYYY') : 'Start unknown'} – {row.endDate ? moment(row.endDate).format('MMM YYYY') : 'Present'}
                  </Typography>
                  <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{row.description}</Typography>
                </Box>
              ))}
              {!detail.data.workHistory.length ? <Typography color="text.secondary" sx={{ py: 2 }}>No work history provided.</Typography> : null}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 0, p: 3 }}>
            <Typography component="h2" variant="h6">Education</Typography>
            <Stack divider={<Divider flexItem />}>
              {detail.data.education.map((row) => (
                <Box key={row.schoolId} sx={{ py: 2 }}>
                  <Typography fontWeight={800}>{row.name}</Typography>
                  <Typography color="text.secondary">{row.degree || 'Degree not listed'}</Typography>
                </Box>
              ))}
              {!detail.data.education.length ? <Typography color="text.secondary" sx={{ py: 2 }}>No education provided.</Typography> : null}
            </Stack>
          </Paper>
        </Stack>
      ) : null}

      {!blocked && dialogOpen ? (
        <IntroductionDialog
          rguid={rguid}
          candidateName={detail.data?.displayName || 'This candidate'}
          access={access.data}
          onClose={() => setDialogOpen(false)}
          onCompleted={(request) => {
            setSubmitted(request);
            setDialogOpen(false);
          }}
        />
      ) : null}
    </DashboardPage>
  );
};

export default ResumeDatabaseDetail;
